// Through the barrel, like RangeCatalog and BloodworkRange: PanelRange.ts
// imports from '.', so reaching BloodworkRange directly would make this module
// the entry point of that cycle. Nothing here runs at module-evaluation time.
import { BloodworkRange, PanelRange } from '.';
import { RANGE_BOUNDS, RangeBound, scopeKeyFor, scopeOf } from './RangeCatalog';

/**
 * One practitioner's own numbers for one population row of one marker
 * ("My Ranges"). See my-ranges-spec.md.
 *
 * SPARSE on purpose: only the bounds the practitioner actually changed are
 * stored. A practitioner who cares about optimalMax keeps tracking LabSmarts
 * on the other five, so a correction we make later still reaches them. The
 * cost is that editing the lab range moves a derived alarm bound underneath
 * them, which the editor predicts by name at save time rather than hiding.
 *
 * Every number is canonical US conventional, exactly like BloodworkRange and
 * Bloodwork. Conversion happens at the editor boundary and nowhere else.
 */
export class RangeOverride {
  /** The MARKER's attribute, phase stripped, as scopeKeyFor() files it. */
  attribute: string = '';

  /**
   * The population this replaces, as `scopeKeyFor()` builds it. Semantic, and
   * deliberately NOT the row id: the table is keyed on (id, attribute) with id
   * not unique, and ids have been rekeyed before. A key either matches a row
   * that still means what it meant or matches nothing, and "nothing" is
   * reported to the practitioner rather than guessed at.
   */
  scopeKey: string = '';

  /** Diagnostic only. Never used for matching — see scopeKey. */
  sourceRangeId?: number;

  labMin?: number;
  labMax?: number;
  optimalMin?: number;
  optimalMax?: number;
  alarmMin?: number;
  alarmMax?: number;

  /** ISO-8601. Drives the "Changed 12 Sep 2026" line. */
  updatedDate: string = '';

  /**
   * The practitioner's own reason, in their words, typed in the save dialog
   * and optional. One line per population.
   *
   * It is the only thing in My Ranges that produces new content rather than
   * new numbers, and it exists because a range a practitioner cannot explain
   * is a range they cannot defend to a colleague. LabSmarts' own citation
   * stays visible beside it, greyed, so the card says who stands behind
   * which number.
   *
   * Deliberately NOT in rangeOverridesSignature(): it changes nothing about
   * how a report is graded, and putting it there would re-run init() over
   * 3,300 rows every time someone fixes a typo in it.
   */
  note?: string;

  constructor(override?: Partial<RangeOverride>) {
    if (!override) {
      return;
    }
    this.attribute = String(override.attribute || '');
    this.scopeKey = String(override.scopeKey || '');
    const sourceRangeId = Number(override.sourceRangeId);
    if (Number.isFinite(sourceRangeId)) {
      this.sourceRangeId = sourceRangeId;
    }
    RANGE_BOUNDS.forEach(bound => {
      const value = Number(override[bound]);
      // A stored '' or null means "this bound is LabSmarts'", which is the
      // same thing as the field being absent. Normalized to absent here so
      // nothing downstream has to tell the two apart.
      if (override[bound] != null && override[bound] !== ('' as any) && Number.isFinite(value)) {
        this[bound] = value;
      }
    });
    this.updatedDate = String(override.updatedDate || '');
    const note = String(override.note || '').trim();
    if (note) {
      this.note = note;
    }
  }

  /** The bounds this override carries, in bar-graph order. */
  boundsSet?(): RangeBound[] {
    return RANGE_BOUNDS.filter(bound => this[bound] != null);
  }
}

/**
 * Whether an override is worth keeping: it names a marker and a population and
 * it changes at least one number. Anything else is a half-written row that
 * would apply nothing, and is dropped on the way out of the profile.
 */
export function isUsableRangeOverride(override?: Partial<RangeOverride>): boolean {
  if (!override || !override.attribute || !override.scopeKey) {
    return false;
  }
  return RANGE_BOUNDS.some(bound => {
    const value = override[bound];
    return value != null && Number.isFinite(Number(value));
  });
}

/**
 * Ceiling on how many a profile may hold. Overrides share the 400KB DynamoDB
 * item with customMarkerLibrary and one is roughly 150 bytes, so the pilot's
 * five markers are nowhere near it; this is the guard that keeps a widened
 * allowlist from quietly filling the item. See my-ranges-spec.md §3.3.
 */
export const RANGE_OVERRIDE_LIMIT = 200;

/** The overrides that can be applied, keyed by the population they replace. */
export function rangeOverridesByScopeKey(
  overrides: RangeOverride[] = [],
): Map<string, RangeOverride> {
  const byScopeKey = new Map<string, RangeOverride>();
  (overrides || []).forEach(override => {
    if (isUsableRangeOverride(override)) {
      // Last one wins. Two overrides for one population should be impossible
      // (the editor writes one per scope key), and if it ever happens the
      // newest is the one the practitioner last saw on the page.
      byScopeKey.set(override.scopeKey, override);
    }
  });
  return byScopeKey;
}

/** The key a stored row answers to. */
export function scopeKeyForRow(row: Partial<PanelRange>): string {
  return scopeKeyFor(row.attribute || '', scopeOf(row));
}

/**
 * The stored rows with this practitioner's numbers written over them, ready
 * for bloodworkRangesFrom().
 *
 * COPIES the rows it changes and leaves the rest alone, so
 * `storedBloodworkRanges` stays the pristine LabSmarts table — the My Ranges
 * page reads it to tell a number LabSmarts authored from one init() derived,
 * and that distinction is gone the moment anything writes through to it.
 *
 * Applied BEFORE init(), which is load-bearing: init() derives optimal, alarm,
 * mean and stdDev from whatever is present, so the practitioner's numbers are
 * what the arithmetic is built around rather than something the override has
 * to undo afterwards.
 */
export function applyRangeOverrides(
  storedRows: BloodworkRange[] = [],
  overrides: RangeOverride[] = [],
): BloodworkRange[] {
  const byScopeKey = rangeOverridesByScopeKey(overrides);
  if (byScopeKey.size === 0) {
    return storedRows || [];
  }
  return (storedRows || []).map(row => {
    if (!row || !row.attribute) {
      return row;
    }
    const override = byScopeKey.get(scopeKeyForRow(row));
    if (!override) {
      return row;
    }
    const applied = new BloodworkRange(row);
    RANGE_BOUNDS.forEach(bound => {
      if (override[bound] != null) {
        applied[bound] = override[bound];
      }
    });
    // What LabSmarts resolves the same row to, which is only knowable here:
    // by the time a bar graph has the row, the practitioner's numbers are the
    // row. init() is run on a second copy so the comparison is like for like,
    // a derived bound against a derived bound, and only for rows that carry
    // an override.
    const labSmartsRow = new BloodworkRange(row).init();
    const labSmarts: Partial<Record<RangeBound, number>> = {};
    RANGE_BOUNDS.forEach(bound => {
      labSmarts[bound] = labSmartsRow[bound];
    });
    applied.appliedRangeOverride = {
      source: 'practitioner',
      scopeKey: override.scopeKey,
      changedBounds: RANGE_BOUNDS.filter(bound => override[bound] != null),
      changedDate: override.updatedDate || undefined,
      note: override.note || undefined,
      labSmarts,
    };
    return applied;
  });
}

/**
 * Overrides whose population no longer exists in the table.
 *
 * A re-banded row (ferritin women 19+ becoming 18+) changes its scope key, so
 * the override stops matching instead of landing on a row whose meaning moved
 * underneath it. Those are dropped, never applied — and REPORTED, because a
 * stale override that disappears with no notice is the same bug as one applied
 * wrongly.
 */
export function staleRangeOverrides(
  storedRows: BloodworkRange[] = [],
  overrides: RangeOverride[] = [],
): RangeOverride[] {
  const usable = (overrides || []).filter(isUsableRangeOverride);
  if (usable.length === 0) {
    return [];
  }
  const live = new Set<string>();
  (storedRows || []).forEach(row => {
    if (row && row.attribute) {
      live.add(scopeKeyForRow(row));
    }
  });
  // An empty table is the range request having failed or not landed yet, which
  // is not evidence that anything is stale.
  if (live.size === 0) {
    return [];
  }
  return usable.filter(override => !live.has(override.scopeKey));
}

/**
 * A signature that changes whenever the applied result would change.
 *
 * Applying overrides means re-running init() over every row of a 3,300-row
 * table, so the result is memoized against this rather than rebuilt per call.
 */
export function rangeOverridesSignature(overrides: RangeOverride[] = []): string {
  return (overrides || [])
    .filter(isUsableRangeOverride)
    .map(override =>
      [
        override.scopeKey,
        ...RANGE_BOUNDS.map(bound =>
          override[bound] == null ? '' : String(override[bound]),
        ),
      ].join('|'),
    )
    .sort()
    .join(';');
}
